import { Bell, CheckCheck, Loader2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useNavigate } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadNotificationCount,
} from "@/hooks/use-notifications";
import { cn } from "@/lib/utils";
import { HEADER_TOOL_BTN_CLASS, HEADER_TOOL_ICON_CLASS } from "@/components/layout/shell/HeaderToolButton";
import type { Notification } from "@/api/notification.api";

interface NotificationBellProps {
  className?: string;
}

function NotificationRow({
  item,
  onOpen,
}: {
  item: Notification;
  onOpen: (item: Notification) => void;
}) {
  const isUnread = !item.read_at;
  const timeAgo = (() => {
    try {
      return formatDistanceToNow(new Date(item.created_at), { addSuffix: true });
    } catch {
      return "";
    }
  })();

  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      className={cn(
        "w-full text-left rounded-lg px-3 py-2.5 transition-colors",
        isUnread ? "bg-primary/8 hover:bg-primary/12" : "hover:bg-muted/60",
      )}
    >
      <div className="flex items-start gap-2">
        {isUnread && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />}
        <div className={cn("min-w-0 flex-1", !isUnread && "pl-4")}>
          <p className={cn("text-sm leading-snug", isUnread ? "font-semibold text-foreground" : "font-medium text-muted-foreground")}>
            {item.title}
          </p>
          {item.body && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{item.body}</p>
          )}
          {timeAgo && <p className="text-[10px] text-muted-foreground/70 mt-1">{timeAgo}</p>}
        </div>
      </div>
    </button>
  );
}

export function NotificationBell({ className }: NotificationBellProps) {
  const navigate = useNavigate();
  const { data: unreadCount = 0 } = useUnreadNotificationCount();
  const { data, isLoading, isFetching } = useNotifications({ unread: false, per_page: 15 });
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const notifications = data?.data ?? [];
  const hasUnread = unreadCount > 0;

  const handleOpen = (item: Notification) => {
    if (!item.read_at) {
      markRead.mutate(item.id);
    }
    if (item.action_url) {
      navigate(item.action_url);
    }
  };

  return (
    <Popover>
      <PopoverTrigger
        aria-label="Notifications"
        className={cn(
          HEADER_TOOL_BTN_CLASS,
          "relative bg-card text-muted-foreground ring-border hover:bg-muted/50 hover:text-foreground",
          className,
        )}
      >
        <Bell className={HEADER_TOOL_ICON_CLASS} />
        {hasUnread && (
          <span className="absolute right-1.5 top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground ring-2 ring-background">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </PopoverTrigger>

      <PopoverContent align="end" className="w-80 p-0 border border-border shadow-lg bg-popover">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-foreground">Notifications</p>
            {hasUnread && (
              <p className="text-xs text-muted-foreground">{unreadCount} unread</p>
            )}
          </div>
          {hasUnread && (
            <button
              type="button"
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isPending}
              className="flex items-center gap-1 text-xs font-medium text-primary hover:underline disabled:opacity-50"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Mark all read
            </button>
          )}
        </div>

        <div className="max-h-80 overflow-y-auto p-1.5">
          {isLoading || isFetching ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="py-10 text-center">
              <Bell className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm font-medium text-foreground">No notifications</p>
              <p className="text-xs text-muted-foreground mt-1">You&apos;re all caught up</p>
            </div>
          ) : (
            notifications.map((item) => (
              <NotificationRow key={item.id} item={item} onOpen={handleOpen} />
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
